/**
 * Copies a texture's texels into a render target, one texel to each pixel, with no filter. A
 * placement maps each target pixel to a source texel, so one pass serves a whole copy, a crop and a
 * copy with its rows reversed. A transfer converts between the plain and the decoding view of a
 * sub-viewport target.
 */
import { drawFullScreen, fullScreenPass } from './fullScreenPass';
import { perObject } from '../utils/perObject';
/** Every texel of a `width` x `height` source, in place. */
export function wholeTexels(width, height) {
    return { offsetX: 0, offsetY: 0, rowStep: 1, bounds: [0, 0, width, height] };
}
/** Every texel of a `width` x `height` source, each row from its mirror row. */
export function reversedTexelRows(width, height) {
    return { offsetX: 0, offsetY: height - 1, rowStep: -1, bounds: [0, 0, width, height] };
}
const TRANSFERS = {
    none: 'texel',
    decode: 'vec4( sRGBTransferEOTF( texel ).rgb, texel.a )',
    encode: 'vec4( sRGBTransferOETF( texel ).rgb, texel.a )',
};
const copyFragment = (transfer) => /* glsl */ `
uniform sampler2D source;
uniform ivec3 placement;
uniform ivec4 bounds;
void main() {
  ivec2 pixel = ivec2(gl_FragCoord.xy);
  ivec2 at = ivec2(pixel.x + placement.x, pixel.y * placement.z + placement.y);
  bool inside = all(greaterThanEqual(at, bounds.xy)) && all(lessThan(at, bounds.zw));
  vec4 texel = inside ? texelFetch(source, clamp( at, ivec2( 0 ), textureSize( source, 0 ) - 1 ), 0) : vec4(0.0);
  gl_FragColor = ${TRANSFERS[transfer]};
}`;
/**
 * The passes of one renderer, one per transfer, not per copy: three leaves a dispose listener that
 * closes over the renderer on each material it draws.
 */
const copyPassesFor = perObject(() => ({
    none: copyPass('none'),
    decode: copyPass('decode'),
    encode: copyPass('encode'),
}));
function copyPass(transfer) {
    return fullScreenPass(`TexelCopy:${transfer}`, copyFragment(transfer), {
        source: { value: null },
        placement: { value: [0, 0, 1] },
        bounds: { value: [0, 0, 0, 0] },
    });
}
/**
 * Draws `source`'s texels into `target`, or into the drawing buffer for null, placed by `placement`
 * and converted by `transfer`. The pass holds no texture once the draw ends.
 *
 * @canonical Copy a texture's texels into a render target
 */
export function copyTexels(gl, source, target, placement, transfer = 'none') {
    const pass = copyPassesFor(gl)[transfer];
    pass.uniforms.source.value = source;
    // Written in place: a copy runs every frame, and a new array each draw is garbage each frame.
    const at = pass.uniforms.placement.value;
    at[0] = placement.offsetX;
    at[1] = placement.offsetY;
    at[2] = placement.rowStep;
    pass.uniforms.bounds.value = placement.bounds;
    try {
        drawFullScreen(gl, pass, target);
    }
    finally {
        pass.uniforms.source.value = null;
    }
}
//# sourceMappingURL=texelCopy.js.map